import { create } from "zustand";

// Estados válidos de un comando según la documentación
export type CommandStatus =
  | "pending_route"
  | "queued_for_route"
  | "queued"
  | "sending"
  | "sent"
  | "ack"
  | "timeout"
  | "offline"
  | "unsupported"
  | "error"
  | "rejected"
  | "expired"
  | "write_failed";

// Interfaz para un comando recibido del socket
export interface SocketCommand {
  id: string;
  device_id: string;
  company_id: string | null;
  vehicle_id: string | null;
  remolque_id: string | null;
  request_id: string;
  imei: string | null;
  brand_id: string | null;
  brand: string | null;
  model: string | null;
  command_doc_id: string | null;
  command_code: string;
  command_name: string | null;
  status: CommandStatus;
  error: string | null;
  raw_request: Record<string, unknown> | null;
  raw_response: Record<string, unknown> | null;
  sent_at: number | null;
  finished_at: number | null;
  receivedAt: number;
}

// Payload del evento command_status del socket
export interface CommandStatusPayload {
  device_id: string;
  company_id?: string | null;
  vehicle_id?: string | null;
  remolque_id?: string | null;
  command: {
    request_id: string;
    imei?: string;
    brand_id?: string;
    brand?: string;
    model?: string;
    command_doc_id?: string;
    command_code: string;
    command_name?: string;
    password_required?: boolean;
    data?: boolean;
    response_expected?: boolean;
    transport?: {
      mode: string;
      value: string;
      channel: string;
    };
    status: CommandStatus;
    edge_id?: string;
    edge_type?: string;
    protocol_family?: string;
    raw_request?: Record<string, unknown> | null;
    raw_response?: Record<string, unknown> | null;
    sent_at?: number | null;
    finished_at?: number | null;
    error?: string | null;
  };
}

// Labels para los estados de comandos
export const COMMAND_STATUS_LABELS: Record<CommandStatus, string> = {
  pending_route: "En espera de conexión",
  queued_for_route: "En cola (unidad desconectada)",
  queued: "En cola",
  sending: "Enviando",
  sent: "Enviado",
  ack: "Confirmado",
  timeout: "Tiempo agotado",
  offline: "Sin conexión",
  unsupported: "No soportado",
  error: "Error",
  rejected: "Rechazado",
  expired: "Expirado",
  write_failed: "Error de escritura",
};

// Colores para los estados
export const COMMAND_STATUS_COLORS: Record<CommandStatus, { bg: string; text: string; darkBg: string; darkText: string }> = {
  pending_route: { bg: "bg-cyan-100", text: "text-cyan-700", darkBg: "bg-cyan-900/30", darkText: "text-cyan-400" },
  queued_for_route: { bg: "bg-amber-100", text: "text-amber-700", darkBg: "bg-amber-900/30", darkText: "text-amber-400" },
  queued: { bg: "bg-blue-100", text: "text-blue-700", darkBg: "bg-blue-900/30", darkText: "text-blue-400" },
  sending: { bg: "bg-yellow-100", text: "text-yellow-700", darkBg: "bg-yellow-900/30", darkText: "text-yellow-400" },
  sent: { bg: "bg-indigo-100", text: "text-indigo-700", darkBg: "bg-indigo-900/30", darkText: "text-indigo-400" },
  ack: { bg: "bg-green-100", text: "text-green-700", darkBg: "bg-green-900/30", darkText: "text-green-400" },
  timeout: { bg: "bg-orange-100", text: "text-orange-700", darkBg: "bg-orange-900/30", darkText: "text-orange-400" },
  offline: { bg: "bg-gray-100", text: "text-gray-700", darkBg: "bg-gray-800", darkText: "text-gray-400" },
  unsupported: { bg: "bg-purple-100", text: "text-purple-700", darkBg: "bg-purple-900/30", darkText: "text-purple-400" },
  error: { bg: "bg-red-100", text: "text-red-700", darkBg: "bg-red-900/30", darkText: "text-red-400" },
  rejected: { bg: "bg-red-100", text: "text-red-700", darkBg: "bg-red-900/30", darkText: "text-red-400" },
  expired: { bg: "bg-orange-100", text: "text-orange-700", darkBg: "bg-orange-900/30", darkText: "text-orange-400" },
  write_failed: { bg: "bg-red-100", text: "text-red-700", darkBg: "bg-red-900/30", darkText: "text-red-400" },
};

// Función para obtener la etiqueta del estado
export const getCommandStatusLabel = (status: CommandStatus): string => {
  return COMMAND_STATUS_LABELS[status] || status;
};

// El `status` es la única fuente de verdad para éxito/fallo. Estos estados representan
// que el comando fue aceptado o está en curso (no es un fallo). Cualquier otro estado
// (timeout, offline, unsupported, error, rejected, expired, write_failed) es fallo.
export const isSuccessStatus = (status: CommandStatus): boolean =>
  ["pending_route", "queued_for_route", "queued", "sending", "sent", "ack"].includes(status);

interface DeviceCommandsStore {
  // Almacena comandos por device_id (máximo 50 comandos por device)
  commandsByDevice: Record<string, SocketCommand[]>;

  // Máximo de comandos a almacenar por device
  maxCommandsPerDevice: number;

  // Agregar/actualizar comando desde socket
  addCommand: (payload: CommandStatusPayload) => void;

  // Obtener comandos de un device
  getDeviceCommands: (deviceId: string) => SocketCommand[];

  // Limpiar comandos de un device
  clearDeviceCommands: (deviceId: string) => void;

  // Limpiar todos los comandos
  clearAllCommands: () => void;
}

export const useDeviceCommandsStore = create<DeviceCommandsStore>((set, get) => ({
  commandsByDevice: {},
  maxCommandsPerDevice: 50,

  addCommand: (payload) => {
    const deviceId = payload.device_id;
    const { command } = payload;
    if (!deviceId || !command.request_id) return;

    set((state) => {
      const currentCommands = state.commandsByDevice[deviceId] || [];

      // Buscar si ya existe el comando por request_id
      const existingIndex = currentCommands.findIndex(
        (cmd) => cmd.request_id === command.request_id
      );

      let updatedCommands: SocketCommand[];

      if (existingIndex !== -1) {
        // Actualizar comando existente
        updatedCommands = [...currentCommands];
        updatedCommands[existingIndex] = {
          ...updatedCommands[existingIndex],
          status: command.status,
          error: command.error ?? updatedCommands[existingIndex].error,
          raw_request: command.raw_request ?? updatedCommands[existingIndex].raw_request,
          raw_response: command.raw_response ?? updatedCommands[existingIndex].raw_response,
          sent_at: command.sent_at ?? updatedCommands[existingIndex].sent_at,
          finished_at: command.finished_at ?? updatedCommands[existingIndex].finished_at,
          receivedAt: Date.now(),
        };
      } else {
        // Crear nuevo comando
        const newCommand: SocketCommand = {
          id: `${deviceId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          device_id: deviceId,
          company_id: payload.company_id ?? null,
          vehicle_id: payload.vehicle_id ?? null,
          remolque_id: payload.remolque_id ?? null,
          request_id: command.request_id,
          imei: command.imei ?? null,
          brand_id: command.brand_id ?? null,
          brand: command.brand ?? null,
          model: command.model ?? null,
          command_doc_id: command.command_doc_id ?? null,
          command_code: command.command_code,
          command_name: command.command_name ?? null,
          status: command.status,
          error: command.error ?? null,
          raw_request: command.raw_request ?? null,
          raw_response: command.raw_response ?? null,
          sent_at: command.sent_at ?? null,
          finished_at: command.finished_at ?? null,
          receivedAt: Date.now(),
        };

        updatedCommands = [newCommand, ...currentCommands].slice(
          0,
          state.maxCommandsPerDevice
        );
      }

      const newCommandsByDevice = {
        ...state.commandsByDevice,
        [deviceId]: updatedCommands,
      };

      // Limitar a 100 devices con comandos
      const deviceKeys = Object.keys(newCommandsByDevice);
      if (deviceKeys.length > 100) {
        const sortedKeys = deviceKeys.sort((a, b) => {
          const aLast = newCommandsByDevice[a]?.[0]?.receivedAt ?? 0;
          const bLast = newCommandsByDevice[b]?.[0]?.receivedAt ?? 0;
          return bLast - aLast;
        });
        const keysToRemove = sortedKeys.slice(100);
        for (const key of keysToRemove) {
          delete newCommandsByDevice[key];
        }
      }

      return { commandsByDevice: newCommandsByDevice };
    });
  },

  getDeviceCommands: (deviceId) => {
    return get().commandsByDevice[deviceId] || [];
  },

  clearDeviceCommands: (deviceId) => {
    set((state) => {
      const { [deviceId]: _, ...rest } = state.commandsByDevice;
      return { commandsByDevice: rest };
    });
  },

  clearAllCommands: () => {
    set({ commandsByDevice: {} });
  },
}));
